import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import { ArrowUpRight } from "lucide-react";
import { ITAR_EXEMPTION_GROUPS } from "@/data/itarExemptions";

// ITAR License Exemptions — top-level menu.
// Mirrors the EAR Exceptions page but grouped by regulatory purpose
// (temporary movement, reexports, training, technical data, country-specific,
// deemed reexports, USG/FMS). Sections that house multiple numbered
// exemptions (§ 123.4, § 123.16(b), § 125.4(b), § 125.5, § 126.5) expose
// each numbered sub-exemption inline.
export default function ITARExemptions() {
  const totalCount = ITAR_EXEMPTION_GROUPS.reduce(
    (n, g) => n + g.items.reduce((m, i) => m + 1 + (i.subs ? i.subs.length : 0), 0),
    0,
  );

  return (
    <Layout>
      <SEO
        title="ITAR License Exemptions — Index"
        description="A browsable index of every ITAR License Exemption under 22 CFR Parts 123, 124, 125, and 126 — including nested exemptions under § 123.16(b), § 125.4(b), § 125.5, § 126.5, and § 126.18. Each entry links to a dedicated compliance page."
        path="/tools/itar-license-exemptions"
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="itar-exemptions-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>Reference · 22 CFR Parts 123, 124, 125, 126</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">ITAR License Exemptions.</h1>
              <p className="mt-6 text-ink-soft italic font-serif text-lg leading-snug max-w-2xl">
                Every ITAR License Exemption on the State Department side of the export-control system, indexed and cross-linked.
              </p>
              <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                License Exemptions authorize exports, reexports, retransfers, and temporary imports of defense articles and defense services that would otherwise require an approved license or agreement from the Directorate of Defense Trade Controls. Because several ITAR sections group multiple numbered exemptions under one paragraph, each numbered exemption is listed separately below.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num" data-testid="itar-exemptions-count">{totalCount}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">exemptions indexed</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3">
                Source · 22 CFR Parts 123 – 126
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Groups */}
      {ITAR_EXEMPTION_GROUPS.map((group, groupIndex) => (
        <section
          key={group.id}
          className={`border-b border-hairline ${groupIndex % 2 === 1 ? "bg-ivory-alt" : ""}`}
          data-testid={`itar-exemptions-group-${group.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <Eyebrow>{group.label}</Eyebrow>
            <p className="mt-4 text-ink-soft font-serif italic text-lg leading-snug max-w-3xl">
              {group.kicker}
            </p>

            <ul className="mt-10 border-t border-hairline">
              {group.items.map((item) => (
                <li key={item.slug} className="border-b border-hairline">
                  <Link
                    to={`/tools/itar-license-exemptions/${item.slug}`}
                    className="group grid grid-cols-12 gap-6 py-7 items-baseline hover:bg-ivory transition-colors -mx-3 px-3"
                    data-testid={`itar-exemption-card-${item.slug}`}
                  >
                    <div className="col-span-3 sm:col-span-2">
                      <p className="font-mono text-[15px] font-semibold text-brass-deep">{item.code}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-mono">{item.cfr.replace("22 CFR ", "")}</p>
                    </div>
                    <div className="col-span-9 sm:col-span-9">
                      <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors leading-tight">
                        {item.name}
                      </h3>
                      <p className="mt-2 text-ink-soft text-[15px] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="hidden sm:block col-span-1 text-right">
                      <ArrowUpRight className="w-4 h-4 inline-block text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                    </div>
                  </Link>

                  {item.subs && (
                    <ul className="pl-3 sm:pl-[16.6666%] pb-6 -mt-1">
                      {item.subs.map((sub) => (
                        <li key={sub.slug} className="border-l-2 border-brass/40 pl-4 py-2">
                          <Link
                            to={`/tools/itar-license-exemptions/${sub.slug}`}
                            className="group flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 hover:bg-ivory transition-colors -ml-2 pl-2 pr-2 py-1"
                            data-testid={`itar-exemption-sub-${sub.slug}`}
                          >
                            <span className="font-mono text-[12px] font-semibold text-brass-deep whitespace-nowrap sm:w-40 shrink-0">
                              {sub.cfr.replace("22 CFR § ", "§ ")}
                            </span>
                            <span className="text-[14px] text-ink group-hover:text-brass transition-colors leading-snug">
                              {sub.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      {/* Disclaimer */}
      <section className="bg-ink text-ivory" data-testid="itar-exemptions-disclaimer">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            Descriptions summarize the regulation and are not the authoritative text. Compliance checklists for each exemption are being layered in over time. Verify every use against the eCFR and consult counsel before relying on any exemption.
          </p>
        </div>
      </section>
    </Layout>
  );
}
